// src/utils/scheduleLogic.js
// Motor de cálculo de disponibilidade da agenda
// Responsável por determinar quais slots estão disponíveis levando em conta:
//   - Janelas de bloqueio aprovadas (windows)
//   - Agendamentos existentes (com duração)
//   - Horário de funcionamento padrão

export const WORK_START = '08:00';
export const WORK_END   = '18:00';

// ── Converters ────────────────────────────────────────────────
export const timeToMin = (t = '00:00') => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

export const minToTime = (m) => {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

// ── Slot generation ───────────────────────────────────────────
// Gera lista de horários em intervalos de 30 min dentro de um range
export const slotsInRange = (start = WORK_START, end = WORK_END) => {
  const slots = [];
  let cur = timeToMin(start);
  const fin = timeToMin(end);
  while (cur + 30 <= fin) {
    slots.push(minToTime(cur));
    cur += 30;
  }
  return slots;
};

export const ALL_DAY_SLOTS = slotsInRange(WORK_START, WORK_END);

// ── Overlap checkers ─────────────────────────────────────────
// Verifica se [slotStart, slotStart+duration) colide com [from, to)
export const overlaps = (slotTime, durationMin, from, to) => {
  const s = timeToMin(slotTime);
  const e = s + durationMin;
  const f = timeToMin(from);
  const t = timeToMin(to);
  return s < t && e > f;
};

// Verifica colisão com uma janela de bloqueio
export const slotBlockedByWindow = (slotTime, durationMin, win) =>
  overlaps(slotTime, durationMin, win.startTime, win.endTime);

// Verifica colisão com um agendamento existente
export const slotBlockedByAppt = (slotTime, durationMin, appt) =>
  overlaps(slotTime, durationMin, appt.time, minToTime(timeToMin(appt.time) + (appt.duration || 60)));

// ── Core: available slots for one professional on one date ────
export const getAvailableSlots = ({
  date,
  professionalId,
  procedureDuration,
  appointments = [],
  windows = [],
  workStart = WORK_START,
  workEnd   = WORK_END,
}) => {
  const safeAppts = appointments || [];
  const safeWins  = windows      || [];

  // Agendamentos que bloqueiam slots: apenas os ativos (done=true libera o slot retroativamente
  // mas não afeta futuros; cancelled libera imediatamente)
  const BLOCKING_STATUSES = new Set(['pending', 'confirmed', 'waitingConfirm']);
  const dayAppts = safeAppts.filter(a =>
    a.date === date &&
    a.professionalId === professionalId &&
    BLOCKING_STATUSES.has(a.status)
  );

  // Janelas de bloqueio aprovadas que afetam esse profissional nessa data
  const dayWins = safeWins.filter(w =>
    w.date === date &&
    w.status === 'approved' &&
    (w.professionalId === professionalId || w.allProfessionals === true)
  );

  // Horário mínimo para hoje: slots no passado ficam indisponíveis
  const now      = new Date();
  const todayISO = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const isToday  = date === todayISO;
  // Adiciona 30 min de antecedência mínima
  const nowMin   = isToday ? now.getHours() * 60 + now.getMinutes() + 30 : 0;

  return slotsInRange(workStart, workEnd).filter(slot => {
    const slotMin = timeToMin(slot);
    const slotEnd = slotMin + procedureDuration;

    // Procedimento não pode ultrapassar o fim do expediente
    if (slotEnd > timeToMin(workEnd)) return false;

    // Slot já passou (ou não tem antecedência mínima de 30 min) no dia atual
    if (isToday && slotMin < nowMin) return false;

    // Bloqueado por janela?
    if (dayWins.some(w => slotBlockedByWindow(slot, procedureDuration, w))) return false;

    // Bloqueado por agendamento existente?
    if (dayAppts.some(a => slotBlockedByAppt(slot, procedureDuration, a))) return false;

    return true;
  });
};

// ── Month availability map ────────────────────────────────────
// Retorna { dayNumber: freeSlotCount } para exibir no calendário
// workDays: array de dias da semana permitidos (0=Dom…6=Sáb). null = todos os dias.
export const getMonthAvailability = ({
  year,
  month,
  professionalId,
  appointments = [],
  windows = [],
  procedureDuration = 30,
  workDays = null, // null = sem restrição por dia da semana
}) => {
  const days = new Date(year, month + 1, 0).getDate();
  const result = {};

  for (let d = 1; d <= days; d++) {
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const dow  = new Date(date + 'T12:00:00').getDay();

    // Dia fora dos dias de funcionamento configurados: 0 slots
    if (workDays !== null && !workDays.includes(dow)) {
      result[d] = 0;
      continue;
    }

    result[d] = getAvailableSlots({
      date,
      professionalId,
      procedureDuration,
      appointments,
      windows,
    }).length;
  }

  return result;
};

// ── Multi-professional availability ──────────────────────────
// Para o fluxo de novo agendamento: dado um procedimento e lista de profissionais,
// retorna { professionalId: [slots] } para um determinado dia
export const getSlotsPerProfessional = ({
  date,
  professionals = [],
  procedureDuration,
  appointments = [],
  windows = [],
}) =>
  Object.fromEntries(
    professionals.map(pro => [
      pro.id,
      getAvailableSlots({
        date,
        professionalId: pro.id,
        procedureDuration,
        appointments,
        windows,
      }),
    ])
  );

// ── Day summary ──────────────────────────────────────────────
// Para o profissional: lista o que está acontecendo num dia (agendamentos + bloqueios)
export const getDaySummary = ({
  date,
  professionalId,
  appointments = [],
  windows = [],
}) => {
  const appts = (appointments || [])
    .filter(a => a.date === date && a.professionalId === professionalId)
    .map(a => ({
      type:      'appointment',
      time:      a.time,
      endTime:   minToTime(timeToMin(a.time) + (a.duration || 60)),
      label:     `${a.procedureName} — ${a.clientName}`,
      status:    a.status,
      id:        a.id,
      raw:       a,
    }));

  const wins = (windows || [])
    .filter(w =>
      w.date === date &&
      w.status !== 'rejected' &&
      (w.professionalId === professionalId || w.allProfessionals)
    )
    .map(w => ({
      type:     'window',
      time:     w.startTime,
      endTime:  w.endTime,
      label:    w.reason || 'Bloqueio',
      status:   w.status, // 'pending' | 'approved'
      id:       w.id,
      raw:      w,
    }));

  return [...appts, ...wins].sort((a, b) => timeToMin(a.time) - timeToMin(b.time));
};

// ── Validate window request ───────────────────────────────────
// Retorna lista de conflitos ao criar uma nova janela
export const validateWindowConflicts = ({
  date,
  startTime,
  endTime,
  professionalId,
  allProfessionals = false,
  appointments = [],
  windows = [],
  excludeWindowId = null,
}) => {
  const conflicts = [];

  // Agendamentos que colidem
  const apptConflicts = appointments.filter(a => {
    if (a.date !== date) return false;
    if (!allProfessionals && a.professionalId !== professionalId) return false;
    if (['cancelled'].includes(a.status)) return false;
    return overlaps(a.time, a.duration || 60, startTime, endTime);
  });

  apptConflicts.forEach(a => {
    conflicts.push({
      type:    'appointment',
      message: `Agendamento de ${a.clientName} (${a.time}) colidirá com esta janela`,
    });
  });

  // Janelas que colidem
  const winConflicts = windows.filter(w => {
    if (w.id === excludeWindowId) return false;
    if (w.date !== date) return false;
    if (w.status === 'rejected') return false;
    if (!allProfessionals && !w.allProfessionals && w.professionalId !== professionalId) return false;
    return overlaps(w.startTime, timeToMin(w.endTime) - timeToMin(w.startTime), startTime, endTime);
  });

  winConflicts.forEach(w => {
    conflicts.push({
      type:    'window',
      message: `Conflito com outra janela: "${w.reason || 'Bloqueio'}" (${w.startTime}–${w.endTime})`,
    });
  });

  return conflicts;
};
